import { useMemo } from 'react';
import { useSites } from '../Sites/queries';
import { useDomains } from '../OfferBlocks/queries';
import { useLandingPagesDtos } from '../LandingPages/queries';
import { useLandingPageGroups } from '../LandingPageGroups/queries';
import { useSequences } from '../Sequences/queries';
import { useOfferSummaries } from '../Offers/queries';
import type { EntityParamKey } from './buildTestLink';
import {
  toDomainOptions,
  toLandingPageOptions,
  toLpgOptions,
  toOfferOptions,
  toSequenceOptions,
  toSiteOptions,
  type DomainOption,
  type PickerList,
  type PickerOption,
} from './linkBuilderOptions';

export interface LinkBuilderOptions {
  sites: PickerList;
  domains: PickerList<DomainOption>;
  entities: Record<EntityParamKey, PickerList>;
}

type Query = { data?: unknown; isLoading: boolean; error?: unknown };

// Each picker gets its own loading/error so one failing list never blanks the whole builder.
const toList = <T extends PickerOption>(query: Query, map: (rows: any[]) => T[]): PickerList<T> => ({
  options: Array.isArray(query.data) ? map(query.data) : [],
  loading: query.isLoading,
  error: !!query.error,
});

export function useLinkBuilderOptions(): LinkBuilderOptions {
  const sites = useSites();
  const domains = useDomains();
  const landingPages = useLandingPagesDtos();
  // The full entities, not the DTOs: only these carry the group's site.
  const lpgs = useLandingPageGroups();
  const sequences = useSequences();
  const offers = useOfferSummaries();

  return useMemo(
    () => ({
      sites: toList(sites, toSiteOptions),
      domains: toList(domains, toDomainOptions),
      entities: {
        lpid: toList(landingPages, toLandingPageOptions),
        lpgid: toList(lpgs, toLpgOptions),
        sequence_id: toList(sequences, toSequenceOptions),
        offer_id: toList(offers, toOfferOptions),
      },
    }),
    // Query objects change identity every render; their data/loading/error are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      sites.data, sites.isLoading, sites.error,
      domains.data, domains.isLoading, domains.error,
      landingPages.data, landingPages.isLoading, landingPages.error,
      lpgs.data, lpgs.isLoading, lpgs.error,
      sequences.data, sequences.isLoading, sequences.error,
      offers.data, offers.isLoading, offers.error,
    ]
  );
}
